import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, Mail, Plus, XCircle } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
  LoadingTableState,
} from '../../../components/StandardTable';
import AdminModal from '../../components/AdminModal';
import AdminListToolbar from '../../components/AdminListToolbar';
import StatusBadge from '../../components/StatusBadge';
import StatusFilter, { LifecycleStatusFilter } from '../../components/StatusFilter';
import ListPager from '../../components/ListPager';
import SearchSelect, { SearchOption } from '../../components/SearchSelect';
import { AdminListResult, useAdminList } from '../../hooks/useAdminList';
import { formatDate, formatDateTime } from '../../lib/format';
import { searchActiveUsers } from '../../lib/pickers';
import { PeopleIcon } from '../../../components/icons';

const filterSelectClasses =
  'h-9 rounded-lg border border-border-light bg-white px-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary';

interface OrgMemberRow {
  membership_id: string;
  role: string;
  member_type: string;
  created_at: string;
  user_id: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  username: string | null;
  user_is_active: boolean;
  email: string | null;
}

const parseMembers = (raw: unknown): AdminListResult<OrgMemberRow> => {
  const data = (raw as {
    data?: { members?: OrgMemberRow[]; pagination?: { total?: number; hasMore?: boolean } };
  }).data;
  return {
    rows: data?.members ?? [],
    total: data?.pagination?.total ?? 0,
    hasMore: data?.pagination?.hasMore ?? false,
  };
};

const memberName = (member: OrgMemberRow): string =>
  member.display_name ||
  [member.first_name, member.last_name].filter(Boolean).join(' ') ||
  member.email ||
  member.user_id;

const COLUMN_COUNT = 7;

const EMPTY_ADD_FORM: { user: SearchOption | null; memberType: string } = {
  user: null,
  memberType: 'staff',
};

/**
 * Members tab of the organization detail page — the standard admin table
 * (search, sortable columns, member-type/role/status filters, pagination)
 * over the org's memberships. Staff and citizen affiliations share the one
 * list, distinguished by the Type column (ACCOUNTS_TOOL_REQUIREMENTS.md).
 * One row per person: the endpoint folds a staff member's dual-access citizen
 * row into their staff row (#1516), so "All types" no longer lists staff
 * twice and the list total matches the header's member counts. Removing a
 * staff row leaves the citizen affiliation, which then shows as Citizen.
 */
const OrgMembersTab: React.FC<{
  orgId: string;
  organizationName: string;
  /** Unfiltered org_admin count (for the last-admin removal warning). */
  orgAdminCount: number;
}> = ({ orgId, organizationName, orgAdminCount }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [memberType, setMemberType] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState<LifecycleStatusFilter>('all');
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_ADD_FORM);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [removeTarget, setRemoveTarget] = useState<{
    membershipId: string;
    memberName: string;
    warning: string;
  } | null>(null);
  const [cancelInvitationTarget, setCancelInvitationTarget] = useState<{ id: string; email: string } | null>(null);

  const list = useAdminList<OrgMemberRow>({
    queryKey: `org-members-${orgId}`,
    endpoint: `/admin/accounts/organizations/${orgId}/members`,
    parse: parseMembers,
    filters: {
      member_type: memberType || undefined,
      role: role || undefined,
      status,
    },
    initialSort: { key: 'name', direction: 'asc' },
  });

  const pendingInvitations = useQuery({
    queryKey: ['admin-accounts', 'org-invitations', orgId],
    queryFn: async () => {
      const response = await api.get(`/organizations/${orgId}/pending-invitations`);
      return response.data as Array<{
        id: string;
        email: string;
        role: string;
        created_at: string;
        expires_at: string;
        invited_by_name: string | null;
      }>;
    },
  });

  const invalidateMembers = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-list'] });
    queryClient.invalidateQueries({ queryKey: ['admin-accounts', 'organization', orgId] });
    queryClient.invalidateQueries({ queryKey: ['admin-accounts', 'org-invitations', orgId] });
    queryClient.invalidateQueries({ queryKey: ['admin-accounts', 'audit', 'organization', orgId] });
  };

  const addMemberMutation = useMutation({
    mutationFn: () =>
      addForm.memberType === 'staff'
        ? api.post(`/organizations/${orgId}/invite-staff`, { userId: addForm.user?.id, role: 'org_admin' })
        : api.post(`/admin/users/${addForm.user?.id}/organizations`, {
            organizationId: orgId,
            memberType: 'citizen',
          }),
    onSuccess: () => {
      toast.success(addForm.memberType === 'staff' ? 'Staff member added' : 'Citizen affiliation added');
      setAddOpen(false);
      setAddForm(EMPTY_ADD_FORM);
      invalidateMembers();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to add member'),
  });

  const inviteByEmailMutation = useMutation({
    mutationFn: () =>
      api.post(`/organizations/${orgId}/invite-staff`, { email: inviteEmail.trim(), role: 'org_admin' }),
    onSuccess: (response) => {
      toast.success(response.data?.message || 'Invitation sent');
      setInviteOpen(false);
      setInviteEmail('');
      invalidateMembers();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to send invitation'),
  });

  const removeMemberMutation = useMutation({
    mutationFn: (membershipId: string) => api.delete(`/admin/user-organizations/${membershipId}`),
    onSuccess: () => {
      toast.success('Member removed');
      setRemoveTarget(null);
      invalidateMembers();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to remove member'),
  });

  const cancelInvitationMutation = useMutation({
    mutationFn: (invitationId: string) => api.delete(`/organizations/${orgId}/invitations/${invitationId}`),
    onSuccess: () => {
      toast.success('Invitation cancelled');
      setCancelInvitationTarget(null);
      invalidateMembers();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to cancel invitation'),
  });

  return (
    <div className="space-y-4">
      <AdminListToolbar
        search={list.search}
        onSearch={list.setSearch}
        searchPlaceholder="Search members by name or email…"
        searchAriaLabel="Search members"
        actions={
          <>
            <Button variant="primary" size="sm" icon={Plus} onClick={() => setAddOpen(true)}>
              Add Member
            </Button>
            <Button variant="outline" size="sm" icon={Mail} onClick={() => setInviteOpen(true)}>
              Invite by Email
            </Button>
          </>
        }
      >
        <select
          value={memberType}
          onChange={(event) => setMemberType(event.target.value)}
          aria-label="Filter by member type"
          className={filterSelectClasses}
        >
          <option value="">All types</option>
          <option value="staff">Staff</option>
          <option value="citizen">Citizen</option>
        </select>
        <select
          value={role}
          onChange={(event) => setRole(event.target.value)}
          aria-label="Filter by role"
          className={filterSelectClasses}
        >
          <option value="">All roles</option>
          <option value="org_admin">org_admin</option>
          <option value="member">member</option>
          <option value="system_admin">system_admin</option>
        </select>
        <StatusFilter value={status} onChange={setStatus} />
      </AdminListToolbar>

      {list.isError ? (
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            Couldn't load members. Please try again.
          </p>
          <Button variant="outline" size="sm" onClick={() => list.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <StandardTable horizontalScroll loading={list.isFetching && !list.isLoading}>
          <StandardTableHeader>
            <StandardTableRow>
              <StandardTableHeaderCell sortable sortKey="name" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Name
              </StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="md" sortable sortKey="email" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Email
              </StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit" sortable sortKey="member_type" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Type
              </StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit" sortable sortKey="role" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Role
              </StandardTableHeaderCell>
              <StandardTableHeaderCell sortable sortKey="user_is_active" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Status
              </StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg" columnType="fit" sortable sortKey="created_at" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Added
              </StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="actions">Actions</StandardTableHeaderCell>
            </StandardTableRow>
          </StandardTableHeader>
          <StandardTableBody>
            {list.isLoading ? (
              <LoadingTableState colSpan={COLUMN_COUNT} />
            ) : list.rows.length === 0 ? (
              <EmptyTableState
                colSpan={COLUMN_COUNT}
                message={list.search ? `No members match "${list.search}"` : 'No members found'}
                icon={<PeopleIcon className="h-8 w-8" />}
              />
            ) : (
              list.rows.map((member) => (
                <StandardTableRow
                  key={member.membership_id}
                  onClick={() => navigate(`/admin-next/accounts/users/${member.user_id}`)}
                >
                  <StandardTableCell>
                    <div className="font-medium text-text-primary dark:text-text-dark-primary">
                      {memberName(member)}
                    </div>
                    {member.username && (
                      <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                        @{member.username}
                      </div>
                    )}
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="md">{member.email ?? '—'}</StandardTableCell>
                  <StandardTableCell>
                    <span
                      className={
                        member.member_type === 'citizen'
                          ? 'inline-flex rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                          : 'inline-flex rounded-full bg-background-secondary px-2 py-0.5 text-xs font-medium text-text-secondary dark:bg-background-dark-tertiary dark:text-text-dark-secondary'
                      }
                    >
                      {member.member_type === 'citizen' ? 'Citizen' : 'Staff'}
                    </span>
                  </StandardTableCell>
                  <StandardTableCell>
                    <span className="rounded-full bg-background-secondary px-2 py-0.5 text-xs text-text-secondary dark:bg-background-dark-tertiary dark:text-text-dark-secondary">
                      {member.role}
                    </span>
                  </StandardTableCell>
                  <StandardTableCell>
                    <StatusBadge active={member.user_is_active !== false} />
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="lg">{formatDate(member.created_at)}</StandardTableCell>
                  <StandardTableCell>
                    <div className="flex items-center" onClick={(event) => event.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={XCircle}
                        onClick={() => {
                          const isOrgAdmin = member.role?.toLowerCase() === 'org_admin';
                          setRemoveTarget({
                            membershipId: member.membership_id,
                            memberName: memberName(member),
                            warning:
                              isOrgAdmin && orgAdminCount <= 1
                                ? ` Warning: this is the organization's last org_admin — removing them leaves "${organizationName}" without an administrator.`
                                : '',
                          });
                        }}
                        ariaLabel={`Remove ${member.email || member.user_id}`}
                      >
                        Remove
                      </Button>
                    </div>
                  </StandardTableCell>
                </StandardTableRow>
              ))
            )}
          </StandardTableBody>
        </StandardTable>
      )}

      {!list.isError && (
        <ListPager
          offset={list.offset}
          limit={list.limit}
          total={list.total}
          page={list.page}
          pageCount={list.pageCount}
          hasPrev={list.hasPrev}
          hasNext={list.hasNext}
          onPrev={list.prevPage}
          onNext={list.nextPage}
          noun="members"
        />
      )}

      {(pendingInvitations.data?.length ?? 0) > 0 && (
        <div className="card p-5">
          <h4 className="mb-2 text-sm font-semibold text-text-primary dark:text-text-dark-primary">
            Pending invitations
          </h4>
          <ul className="divide-y divide-border-light dark:divide-border-dark">
            {pendingInvitations.data!.map((invitation) => (
              <li key={invitation.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                <div>
                  <span className="font-medium text-text-primary dark:text-text-dark-primary">
                    {invitation.email}
                  </span>
                  <span className="ml-2 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                    invited {formatDateTime(invitation.created_at)}
                    {invitation.invited_by_name ? ` by ${invitation.invited_by_name}` : ''} · expires{' '}
                    {formatDateTime(invitation.expires_at)}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={XCircle}
                  onClick={() => setCancelInvitationTarget({ id: invitation.id, email: invitation.email })}
                  ariaLabel={`Cancel invitation for ${invitation.email}`}
                >
                  Cancel
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <AdminModal
        isOpen={addOpen}
        title="Add Member"
        onClose={() => setAddOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!addForm.user}
              loading={addMemberMutation.isLoading}
              onClick={() => addMemberMutation.mutate()}
            >
              Add Member
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
              User *
            </label>
            <SearchSelect
              placeholder="Search active users…"
              fetchOptions={searchActiveUsers}
              selected={addForm.user}
              onSelect={(user) => setAddForm((current) => ({ ...current, user }))}
              onClear={() => setAddForm((current) => ({ ...current, user: null }))}
              ariaLabel="Search users"
            />
          </div>
          <div>
            <label htmlFor="add-member-type" className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
              Membership type
            </label>
            <select
              id="add-member-type"
              value={addForm.memberType}
              onChange={(event) => setAddForm((current) => ({ ...current, memberType: event.target.value }))}
              className="w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
            >
              <option value="staff">Staff (org_admin)</option>
              <option value="citizen">Citizen affiliation</option>
            </select>
            <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Citizen affiliation is what a citizen gets by signing up through this
              organization's signup link — a member-role association with no staff access.
            </p>
          </div>
        </div>
      </AdminModal>

      <AdminModal
        isOpen={inviteOpen}
        title="Invite by Email"
        onClose={() => setInviteOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setInviteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={Mail}
              disabled={!inviteEmail.includes('@')}
              loading={inviteByEmailMutation.isLoading}
              onClick={() => inviteByEmailMutation.mutate()}
            >
              Send Invitation
            </Button>
          </>
        }
      >
        <div className="space-y-2">
          <label htmlFor="invite-email" className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
            Email address
          </label>
          <input
            id="invite-email"
            type="email"
            value={inviteEmail}
            onChange={(event) => setInviteEmail(event.target.value)}
            placeholder="person@example.org"
            className="w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
          />
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            They'll get an email with an accept link and set their own password. If the address
            already has an account, it's added as staff immediately.
          </p>
        </div>
      </AdminModal>

      <ConfirmationDialog
        isOpen={removeTarget !== null}
        title="Remove member"
        message={
          removeTarget
            ? `Remove ${removeTarget.memberName} from "${organizationName}"?${removeTarget.warning}`
            : ''
        }
        confirmText="Remove"
        variant={removeTarget?.warning ? 'danger' : 'warning'}
        onConfirm={() => removeTarget && removeMemberMutation.mutate(removeTarget.membershipId)}
        onCancel={() => setRemoveTarget(null)}
      />

      <ConfirmationDialog
        isOpen={cancelInvitationTarget !== null}
        title="Cancel invitation"
        message={
          cancelInvitationTarget
            ? `Cancel the pending invitation for ${cancelInvitationTarget.email}? The accept link stops working.`
            : ''
        }
        confirmText="Cancel Invitation"
        variant="warning"
        onConfirm={() => cancelInvitationTarget && cancelInvitationMutation.mutate(cancelInvitationTarget.id)}
        onCancel={() => setCancelInvitationTarget(null)}
      />
    </div>
  );
};

export default OrgMembersTab;
